import { z } from "zod";
import { IsoDateTime, PlayerRef } from "../common.ts";
import { ReasonCodeSchema } from "../reasons.ts";
import { IsoDay, PageParams, PeriodBound, PeriodV1, SubjectStatusV1 } from "./common.ts";

// User profile (spec explorer-user-profile) and device profile (spec explorer-device-profile);
// `explorer.read`. Opening a profile is a sensitive read, audited before answering (503 otherwise).

/** Profile routes of one subject: the operator is optional (selector; default the latest seen). */
export const ProfileQueryV1 = z
  .object({ operator_id: z.uuid().optional() })
  .strict()
  .meta({ id: "ProfileQueryV1" });
export type ProfileQueryV1 = z.infer<typeof ProfileQueryV1>;

/** Period routes of one subject; the period defaults to the last 30 days. */
export const SubjectPeriodQueryV1 = z
  .object({
    operator_id: z.uuid().optional(),
    from: PeriodBound.optional(),
    to: PeriodBound.optional(),
  })
  .strict()
  .meta({ id: "SubjectPeriodQueryV1" });
export type SubjectPeriodQueryV1 = z.infer<typeof SubjectPeriodQueryV1>;

const Indicators = {
  transactions: z.int().nonnegative(),
  denied: z.int().nonnegative(),
  failure_rate: z.number().min(0).max(1),
  first_seen_at: IsoDateTime,
  last_seen_at: IsoDateTime,
};

export const UserProfileResponseV1 = z
  .object({
    player_ref: PlayerRef,
    organization_id: z.uuid().nullable(),
    operator_id: z.uuid(),
    /** Allowed operators in which the player appears (the operator selector). */
    operators: z.array(z.uuid()),
    status: SubjectStatusV1,
    indicators: z
      .object({
        ...Indicators,
        /** Highlighted above the environment's `failure_rate_highlight` (default 10%). */
        failure_rate_threshold: z.number().min(0).max(1),
        failure_rate_highlighted: z.boolean(),
        devices: z.int().nonnegative(),
      })
      .strict(),
  })
  .strict()
  .meta({ id: "UserProfileResponseV1" });
export type UserProfileResponseV1 = z.infer<typeof UserProfileResponseV1>;

export const TimelineQueryV1 = z
  .object({
    ...SubjectPeriodQueryV1.shape,
    /** "Error list": only the transactions denied for this reason. */
    reason: ReasonCodeSchema.optional(),
  })
  .strict()
  .meta({ id: "TimelineQueryV1" });
export type TimelineQueryV1 = z.infer<typeof TimelineQueryV1>;

export const TimelineResponseV1 = z
  .object({
    period: PeriodV1,
    reason: ReasonCodeSchema.optional(),
    /** One entry per day of the period (zeros included), oldest first. */
    days: z.array(
      z
        .object({
          /** UTC day of the window's first instant. */
          day: IsoDay,
          /** First instant of the 24-hour window (label it in the user's time zone). */
          start: IsoDateTime,
          approved: z.int().nonnegative(),
          denied: z.int().nonnegative(),
        })
        .strict(),
    ),
  })
  .strict()
  .meta({ id: "TimelineResponseV1" });
export type TimelineResponseV1 = z.infer<typeof TimelineResponseV1>;

export const SubjectPageQueryV1 = z
  .object({ operator_id: z.uuid().optional(), ...PageParams })
  .strict()
  .meta({ id: "SubjectPageQueryV1" });
export type SubjectPageQueryV1 = z.infer<typeof SubjectPageQueryV1>;

/** Devices tab of a user profile. */
export const UserDevicesResponseV1 = z
  .object({
    total: z.int().nonnegative(),
    page: z.int().min(1),
    size: z.int().min(1),
    items: z.array(
      z
        .object({
          device_ref: z.uuid(),
          os: z.string(),
          browser: z.string(),
          browser_version: z.string(),
          /** Transactions of this player on the device. */
          transactions: z.int().nonnegative(),
          first_seen_at: IsoDateTime,
          last_seen_at: IsoDateTime,
          status: SubjectStatusV1,
        })
        .strict(),
    ),
  })
  .strict()
  .meta({ id: "UserDevicesResponseV1" });
export type UserDevicesResponseV1 = z.infer<typeof UserDevicesResponseV1>;

export const DeviceProfileResponseV1 = z
  .object({
    device_ref: z.uuid(),
    operator_id: z.uuid(),
    period: PeriodV1,
    status: SubjectStatusV1,
    /** Attributes of the most recent transaction; screen, time zone and language: 180-day tier. */
    last: z
      .object({
        os: z.string(),
        os_version: z.string().nullable(),
        browser: z.string(),
        browser_version: z.string(),
        screen_resolution: z.string().nullable(),
        timezone: z.string().nullable(),
        language: z.string().nullable(),
      })
      .strict(),
    indicators: z
      .object({
        ...Indicators,
        /** Distinct players in the period (`SharedDevicePolicy`). */
        distinct_players: z.int().nonnegative(),
        shared_device_threshold: z.int().min(1),
        shared_device_highlighted: z.boolean(),
      })
      .strict(),
  })
  .strict()
  .meta({ id: "DeviceProfileResponseV1" });
export type DeviceProfileResponseV1 = z.infer<typeof DeviceProfileResponseV1>;

export const DevicePlayersQueryV1 = z
  .object({ ...SubjectPeriodQueryV1.shape, ...PageParams })
  .strict()
  .meta({ id: "DevicePlayersQueryV1" });
export type DevicePlayersQueryV1 = z.infer<typeof DevicePlayersQueryV1>;

/** Related players of a device in the period. */
export const DevicePlayersResponseV1 = z
  .object({
    period: PeriodV1,
    total: z.int().nonnegative(),
    page: z.int().min(1),
    size: z.int().min(1),
    items: z.array(
      z
        .object({
          player_ref: PlayerRef,
          status: SubjectStatusV1,
          transactions_on_device: z.int().nonnegative(),
          first_seen_on_device: IsoDateTime,
          last_seen_on_device: IsoDateTime,
        })
        .strict(),
    ),
  })
  .strict()
  .meta({ id: "DevicePlayersResponseV1" });
export type DevicePlayersResponseV1 = z.infer<typeof DevicePlayersResponseV1>;
